import {API_BASE_URL} from '@/config/config';

import {
  ACCOUNT_DELETION_CONFIRM_PATH,
  ACCOUNT_DELETION_REQUEST_PATH,
  DEFAULT_CODE_EXPIRY_SECONDS,
  DEFAULT_RETRY_SECONDS
} from './account-deletion.constants';
import type {
  DeletionChallenge,
  DeletionIdentity,
  DeletionResult
} from './account-deletion.types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export class AccountDeletionApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly retryAfterSeconds?: number
  ) {
    super(message);
    this.name = 'AccountDeletionApiError';
  }
}

function getRetryAfterSeconds(response: Response, data: unknown) {
  const header = response.headers.get('Retry-After')?.trim();
  if (header) {
    const seconds = Number(header);
    if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
    const deadline = Date.parse(header);
    if (Number.isFinite(deadline)) {
      return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    }
  }
  if (isRecord(data)) {
    const seconds = data.retryAfterSeconds;
    if (
      typeof seconds === 'number' &&
      Number.isFinite(seconds) &&
      seconds >= 0
    ) {
      return Math.ceil(seconds);
    }
  }
  return undefined;
}

async function postAnonymously(
  path: string,
  body: object,
  signal: AbortSignal
) {
  const response = await fetch(`${API_BASE_URL.replace(/\/$/u, '')}${path}`, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: JSON.stringify(body),
    signal
  });
  const payload: unknown = await response.json().catch(() => undefined);
  const envelope =
    isRecord(payload) && isRecord(payload.data) ? payload.data : undefined;
  const data = envelope?.data;
  const message =
    typeof envelope?.message === 'string' && envelope.message.trim()
      ? envelope.message
      : 'The request could not be completed. Please try again.';

  if (
    !response.ok ||
    !isRecord(payload) ||
    payload.success !== true ||
    envelope?.type !== 'success'
  ) {
    throw new AccountDeletionApiError(
      message,
      response.status,
      getRetryAfterSeconds(response, data)
    );
  }
  return {status: response.status, data};
}

function getTiming(value: unknown, fallback: number, minimum: number) {
  if (value === undefined) return fallback;
  if (typeof value === 'number' && Number.isFinite(value) && value >= minimum) {
    return Math.ceil(value);
  }
  throw new AccountDeletionApiError(
    'The code request returned an unexpected response. Please try again.'
  );
}

export async function requestAccountDeletionCode(
  identity: DeletionIdentity,
  signal: AbortSignal
): Promise<DeletionChallenge> {
  const {status, data} = await postAnonymously(
    ACCOUNT_DELETION_REQUEST_PATH,
    identity,
    signal
  );
  if (
    status !== 202 ||
    !isRecord(data) ||
    typeof data.challengeId !== 'string' ||
    !data.challengeId.trim() ||
    (data.channel !== 'email' && data.channel !== 'sms')
  ) {
    throw new AccountDeletionApiError(
      'The code request returned an unexpected response. Please try again.',
      status
    );
  }
  return {
    challengeId: data.challengeId,
    channel: data.channel,
    destination:
      typeof data.destination === 'string' ? data.destination : undefined,
    expiresInSeconds: getTiming(
      data.expiresInSeconds,
      DEFAULT_CODE_EXPIRY_SECONDS,
      1
    ),
    resendAfterSeconds: getTiming(
      data.resendAfterSeconds,
      DEFAULT_RETRY_SECONDS,
      0
    )
  };
}

export async function confirmAccountDeletion(
  challengeId: string,
  code: string,
  signal: AbortSignal
): Promise<DeletionResult> {
  const {status} = await postAnonymously(
    ACCOUNT_DELETION_CONFIRM_PATH,
    {challengeId, code},
    signal
  );
  if (status === 200) return 'deleted';
  if (status === 202) return 'in-progress';
  throw new AccountDeletionApiError(
    'The deletion request returned an unexpected response. Please try again.',
    status
  );
}
